/**
 * Core-100 R3 — wording for the "Needs attention" surface (records built and proven in Round 2 with no surface:
 * unexpected arrivals CORE-AP, abandoned timers CORE-AJ, run-long episodes CORE-BF) and the due-out outcome card
 * (CORE-BH). Pure, so the words are tested; every label says what the record supports and nothing more.
 */

export type ArrivalIdentityState = 'NO_ACTIVE_VISIT' | 'UNKNOWN_PLATE' | 'LOW_CONFIDENCE' | 'UNREADABLE' | string;

export function describeArrivalIdentity(state: ArrivalIdentityState): string {
  switch (state) {
    case 'NO_ACTIVE_VISIT': return 'A known vehicle arrived, but it has no visit open today.';
    case 'UNKNOWN_PLATE': return 'The camera read a plate this shop has not seen before.';
    case 'LOW_CONFIDENCE': return 'The camera was not sure what it read.';
    case 'UNREADABLE': return 'The camera could not read the plate.';
    default: return 'The camera could not match this arrival to a visit.';
  }
}

export function describeArrivalHints(h: { hint_make?: string | null; hint_model?: string | null; hint_color?: string | null; hint_body_style?: string | null }): string {
  const parts = [h.hint_color, h.hint_make, h.hint_model, h.hint_body_style].filter((x): x is string => !!x && x.trim().length > 0);
  return parts.length > 0 ? parts.join(' ') : 'No description from the camera';
}

export const ARRIVAL_DECISION_LABEL: Record<'linked_to_ticket' | 'not_a_service_visit' | 'could_not_identify', string> = {
  linked_to_ticket: 'It is this visit',
  not_a_service_visit: 'Not a service visit',
  could_not_identify: 'Could not identify',
};

export function describeArrivalRefusal(message: string | undefined): string {
  if (!message) return 'The decision was not recorded.';
  if (message.includes('ARRIVAL_LINK_NEEDS_LIVE_TICKET_OF_THIS_SHOP')) return 'Choose an open visit of this shop to link it to.';
  if (message.includes('ARRIVAL_REVIEW_FRONT_OFFICE_ONLY')) return 'Only the front office reviews arrivals.';
  if (/duplicate key|unique/i.test(message)) return 'Someone already decided this arrival.';
  return 'The decision was not recorded.';
}

/** A run-long episode's cause is stated only as far as the record supports it (R2-46 conservative attribution). */
export function describeRunLongAttribution(attribution: 'additional_work' | 'mixed' | 'undetermined' | string): string {
  switch (attribution) {
    case 'additional_work': return 'Running long because work was added';
    case 'mixed': return 'Running long — partly added work, partly over the estimate';
    default: return 'Running long — the cause is not recorded';
  }
}

export function describeRunLongBaseline(basis: 'frozen_expectation' | 'finish_when_first_seen' | string): string {
  return basis === 'frozen_expectation'
    ? 'against the finish expected when work started'
    : 'against the finish shown when it was first seen running long (the original expectation was not kept)';
}

export function hoursSince(iso: string, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - new Date(iso).getTime()) / 3_600_000));
}

/** An abandoned-timer flag is a notice: the technician (or a manager) decides; nothing stops the timer for them. */
export function describeOpenTimer(startedHoursAgo: number, thresholdHours: number): string {
  return `Running for ${startedHoursAgo} h — longer than this shop's ${thresholdHours} h check. Ask the technician; OptiBay never stops a timer on its own.`;
}

export interface DueOutOutcomes {
  detected: number; detected_at_risk: number; detected_missed: number; open: number;
  kept: number; mitigated: number; missed: number; cancelled: number;
  /** R4: a person changed (or removed) the promise — neither kept nor mitigated. */
  re_promised?: number;
}

/** Rates only over promises that have an outcome; nothing is a percentage of nothing. A re-promise is never "kept". */
export function describeDueOutOutcomes(o: DueOutOutcomes | null): string {
  if (!o || Number(o.detected) === 0) return 'No promise was found at risk in this period.';
  const decided = Number(o.kept) + Number(o.mitigated) + Number(o.missed);
  const rp = Number(o.re_promised ?? 0);
  const rpText = rp > 0 ? ` ${rp} ${rp === 1 ? 'was' : 'were'} re-promised to the customer (not counted as kept).` : '';
  if (decided === 0) return `${o.detected} promise${Number(o.detected) === 1 ? '' : 's'} at risk, none resolved yet.${rpText}`;
  const saved = Number(o.kept) + Number(o.mitigated);
  return `${saved} of ${decided} promise${decided === 1 ? '' : 's'} in trouble were kept (${o.mitigated} after the plan changed); ${o.missed} missed.${rpText}`;
}

/** R4 CORE-BG: the waiting customers' promise, kept or late — a rate only over waiters whose promise could be judged. */
export interface WaiterPromiseSummary {
  waiters: number; kept: number; late: number; no_promise: number; not_completed: number; median_minutes_late: number | null;
}

export function describeWaiterPromises(w: WaiterPromiseSummary | null): string {
  if (!w || Number(w.waiters) === 0) return 'No waiting customer in this period.';
  const judged = Number(w.kept) + Number(w.late);
  const apart = [
    Number(w.no_promise) > 0 ? `${w.no_promise} had no ready time promised` : null,
    Number(w.not_completed) > 0 ? `${w.not_completed} not finished yet` : null,
  ].filter(Boolean).join('; ');
  const head = judged === 0
    ? "No waiting customer's promise can be judged yet."
    : `${w.kept} of ${judged} waiting customer${judged === 1 ? '' : 's'} had the vehicle ready by the promised time` +
      (Number(w.late) > 0 && w.median_minutes_late != null ? `; ${w.late} late (typically by ${w.median_minutes_late} min).` : '.');
  return apart ? `${head} ${apart[0].toUpperCase()}${apart.slice(1)}.` : head;
}

/** R4 CORE-BF: what a run-long visit is doing to the rest of the day, stated from the records. */
export interface RunLongConsequence {
  episode_id: string; ticket_id: string; technician_id: string | null; projected_finish: string;
  late_starts: Array<{ ticket_id: string; number: number | null; type: string | null; planned_start: string; late_minutes: number }>;
  promises: Array<{ ticket_id: string; kind: 'missed' | 'at_risk'; due_out: string }>;
}

export function describeRunLongConsequences(
  c: RunLongConsequence | undefined,
  label: (ticketId: string) => string,
  shiftEnd: Date | null,
): string[] {
  if (!c) return [];
  const out: string[] = [];
  for (const l of c.late_starts) {
    out.push(`${label(l.ticket_id)}${l.type === 'Waiter' ? ' (customer waiting)' : ''} cannot start on time — about ${l.late_minutes} min late.`);
  }
  for (const p of c.promises) {
    out.push(`${label(p.ticket_id)}: promise ${p.kind === 'missed' ? 'already missed' : 'at risk'}.`);
  }
  if (shiftEnd && new Date(c.projected_finish).getTime() > shiftEnd.getTime()) {
    out.push("This visit now runs past the technician's end of day — carrying it is a decision for the shop.");
  }
  return out;
}

/** R4 CORE-BH: an open due-out episode, in words — missed and at risk are different things to do. */
export function describeDueOutEpisode(e: { kind: 'missed' | 'at_risk'; due_out: string; max_projected_late_minutes?: number | null }, timeZone: string): string {
  const at = new Date(e.due_out).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone });
  return e.kind === 'missed'
    ? `Missed — promised for ${at}; the work is still open. Talk to the customer.`
    : `At risk — promised for ${at}; projected about ${Math.max(0, Number(e.max_projected_late_minutes ?? 0))} min late. There is still time to act.`;
}
